/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { classMap } from 'lit/directives/class-map.js';
import { css, html } from 'lit';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import C4DContentBlock, {
  CONTENT_BLOCK_COMPLEMENTARY_STYLE_SCHEME,
} from '../content-block/content-block';
import styles from './content-block-segmented.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Simple version of content block.
 *
 * @element c4d-content-block-segmented
 */
@customElement(`${c4dPrefix}-content-block-segmented`)
class C4DContentBlockSegmented extends C4DContentBlock {
  protected _getContainerClasses() {
    const {
      complementaryStyleScheme,
      _hasComplementary: hasComplementary,
      _hasContent: hasContent,
      _hasCopy: hasCopy,
      _hasHeading: hasHeading,
      _hasMedia: hasMedia,
    } = this;
    return classMap({
      [`${prefix}--content-layout`]: true,
      [`${prefix}--content-layout--with-complementary`]: hasComplementary,
      [`${c4dPrefix}-ce--content-layout--with-adjacent-heading-content`]:
        hasHeading && hasContent && !hasCopy && !hasMedia,
      [`${prefix}--layout--border`]:
        complementaryStyleScheme ===
        CONTENT_BLOCK_COMPLEMENTARY_STYLE_SCHEME.WITH_BORDER,
    });
  }

  protected _renderInnerBody() {
    return html` ${this._renderMedia()}${this._renderContent()} `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--content-block-segmented`;
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static get styles() {
    return css`
      ${super.styles}${styles}
    `;
  }
}

console.warn(
  'The content-block-segmented component has been deprecated in favor of the content-section/block and a variety of group components. ' +
    'See content-section/block documentation for more information.'
);

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DContentBlockSegmented;
